import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import BackgroundDesign from "../components/BackgroundDesign";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faCalendarAlt,
  faSchool,
  faUsers,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

// Helper function to calculate days left until the event
const calculateDaysLeft = (dateString) => {
  if (!dateString) return 0;
  const eventDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  eventDate.setHours(0, 0, 0, 0);
  
  const diffTime = eventDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays > 0 ? diffDays : 0;
};

// Helper function to calculate progress percentage
const calculateProgress = (raised, goal) => {
  if (!goal || goal === 0) return 0;
  const progress = (raised / goal) * 100;
  return Math.min(Math.round(progress * 10) / 10, 100); // Round to 1 decimal, cap at 100%
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const PageHero = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1)),
      url(/images/sliders/SLIDE_HBJ_main.webp) center/cover no-repeat;

    filter: brightness(0.3);
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(
      to top,
      ${(props) => props.theme.colors.background},
      transparent
    );
    z-index: 2;
  }
`;

const PageHeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
  animation: ${fadeUp} 1s ease-out;
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const PageTitle = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  line-height: 1.2;

  span {
    color: ${(props) => props.theme.colors.primary};
    animation: ${pulse} 2s ease-in-out infinite;
    display: inline-block;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  opacity: 0.9;
  margin-bottom: 2rem;
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  padding: 10rem 0;
  position: relative;
  z-index: 1;
`;

const FiltersSection = styled.section`
  max-width: 1200px;
  margin: -10px auto 4rem;
  padding: 1.5rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 3;
  animation: ${fadeUp} 0.8s ease-out;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;

  input {
    flex: 1;
    padding: 1rem 1.5rem;
    border: 2px solid #eee;
    border-radius: 10px;
    font-size: 1rem;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.colors.primary};
      box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary}22;
    }
  }
`;

const CampaignsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
`;

const CampaignCard = styled.div.attrs(props => ({
  style: {
    animationDelay: `${props.index * 0.1}s`,
  }
}))`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${fadeUp} 0.8s ease-out;
  opacity: 0;
  animation-fill-mode: forwards;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }
`;

// Add a spacer div to push the button to the bottom
const Spacer = styled.div`
  flex: 1;
`;

const CampaignImage = styled.div.attrs(props => ({
  style: {
    backgroundImage: `url(${props.image})`,
  }
}))`
  height: 200px;
  background-size: cover;
  background-position: center;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  }
`;

const CampaignContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CampaignTitle = styled.h3`
  font-size: 1.5rem;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 1rem;
  font-weight: 700;
`;

const CampaignMeta = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  color: #666;
  font-size: 0.9rem;

  div {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #eee;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  background: #b5873a;
  height: 100%;
  width: ${(props) => props.$progress || 0}%;
  border-radius: 4px;
  transition: width 1s ease;
`;

const CampaignStats = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;

  div {
    text-align: center;

    h4 {
      font-size: 1.2rem;
      color: ${(props) => props.theme.colors.text};
      margin-bottom: 0.25rem;
    }

    p {
      color: #666;
      font-size: 0.9rem;
    }
  }
`;

const DonateButton = styled(Link)`
  display: block;
  width: 100%;
  background: ${(props) => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px ${(props) => props.theme.colors.primary}33;
  }
`;

const ProjectOverview = styled.section`
  max-width: 1200px;
  margin: -100px auto 6rem;
  padding: 3rem;
  background: white;
  border-radius: 25px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 3;
  animation: ${fadeUp} 0.8s ease-out;
`;

const OverviewTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 2rem;
  text-align: center;

  span {
    color: ${(props) => props.theme.colors.primary};
  }
`;

const OverviewText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: #555;
  text-align: center;
  margin-bottom: 3rem;
`;

const ProjectStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const StatCard = styled.div`
  text-align: center;
  padding: 2rem;
  background: #f8f9fa;
  border-radius: 15px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const StatIcon = styled.div`
  font-size: 3rem;
  color: ${(props) => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const StatNumber = styled.h3`
  font-size: 2.5rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.p`
  font-size: 1.1rem;
  color: #666;
  font-weight: 600;
`;

const PartnersSection = styled.section`
  max-width: 1200px;
  margin: 0 auto 4rem;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  text-align: center;
`;

const PartnersTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 1rem;
  letter-spacing: 0.5px;

  span {
    color: ${(props) => props.theme.colors.primary};
  }
`;

const PartnersSubtitle = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 3rem;
  line-height: 1.6;
`;

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  align-items: center;
  justify-items: center;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1.5rem;
  }
`;

const PartnerLogoContainer = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: white;
  border: 2px solid #eee;
  border-radius: 12px;
  transition: all 0.3s ease;
  text-decoration: none;
  width: 100%;
  height: 120px;

  &:hover {
    transform: translateY(-5px);
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 8px 20px rgba(181, 135, 58, 0.2);
  }
`;

const PartnerLogo = styled.img`
  max-width: 100%;
  max-height: 80px;
  object-fit: contain;
  filter: grayscale(20%);
  transition: filter 0.3s ease;

  ${PartnerLogoContainer}:hover & {
    filter: grayscale(0%);
  }
`;

const SponsorSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2.5rem 2rem;
  background: linear-gradient(to bottom, #fafafa, #ffffff);
  border-radius: 16px;
  margin-top: 3rem;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 8px 24px rgba(181, 135, 58, 0.12);
  }
`;

const SponsorTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #999;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
`;

const SponsorLogo = styled.img`
  max-width: 180px;
  height: auto;
  padding: 1rem 1.5rem;
  background: white;
  border: 2px solid rgba(191, 158, 8, 0.94);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(181, 136, 58, 1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(191, 158, 8, 0.94);
    box-shadow: 0 8px 24px rgba(191, 158, 8, 0.94);
  }
`;

const HeroMeta = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 500;

  svg {
    color: ${(props) => props.theme.colors.primary};
    font-size: 1.2rem;
  }
`;

const projects = [
  {
    id: 1,
    title: "Heartbeat Journey: Spreading Hope, One Step at a Time",
    location: "Casablanca, Morocco",
    date: "Feb 23, 2024",
    image: "/images/sliders/SLIDE_HBJ_main.webp",
    raised: 15000,
    goal: 25000,
    donors: 128,
    daysLeft: 15,
    progress: 60,
    showStats: false, // New property to control stats visibility
    category: "Education",
  },
  {
    id: 2,
    title:
      "1st Action Pilot Project: in Douar Al Byadna",
    location: "Marrakech, Morocco",
    date: "Mar 15, 2024",
    image: "/images/HeartbeatJourney-Actions/hb-v1/SLIDE_HBJv1.webp",
    raised: 25100,
    goal: 30000,
    donors: 75,
    daysLeft: 0,
    progress: 84,
    showStats: true,
    category: "Education",
  },
  {
    id: 3,
    title: "2nd Action Béni Mellal-Khénifra Region",
    location: "Béni Mellal, Morocco",
    date: "Apr 10, 2024",
    image: "/images/HeartbeatJourney-Actions/hb-v2/SLIDE_HBJv2.webp",
    raised: 18000,
    goal: 30000,
    donors: 60,
    daysLeft: 0,
    progress: 60,
    showStats: true,
    category: "Education",
  },
  {
    id: 4,
    title: "3rd Action Douar Dar El Khanous",
    location: "Commune Zitouna, Tangier-Tetouan-Al Hoceima Region, Morocco",
    date: "March 27–29, 2026",
    eventDate: "2026-03-27", // Used for automatic countdown calculation
    image: "/images/sliders/MAIN-SLIDE-HBJv3.webp",
    raised: 33020,
    goal: 150000,
    donors: 58,
    showStats: true,
    category: "Education",
  },
  {
    id: 5,
    title: "4th Action Douar Toufaress",
    location: "Commune Belfaa, Chtouka-Aït Baha, Souss-Massa Region, Morocco",
    date: "November 6–8, 2026",
    eventDate: "2026-11-06", // Used for automatic countdown calculation
    image: "/images/HeartbeatJourney-Actions/hb-v4/SLIDE_HBJv4.png",
    raised: 300,
    goal: 120000, // TODO: confirm the fundraising goal for the 4th edition
    donors: 1,
    showStats: true,
    category: "Education",
  },
];

const HbeatJourneyPage = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredProjects = projects
    .filter(
      (project) =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.location.toLowerCase().includes(searchTerm.toLowerCase())
    );

  return (
    <PageWrapper>
      <BackgroundDesign />
      <SEO
        title="Heartbeat Journey | 24 Schools, 12 Regions | The Big Heart Association Morocco"
        description="Heartbeat Journey is a 7-year mission (2024–2031) by The Big Heart Association to renovate 24 schools across Morocco's 12 regions, bringing education, healthcare, and hope to rural communities."
        url="/heartbeat-journey"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Heartbeat Journey", url: "/heartbeat-journey" }]}
        noOrganizationSchema
      />
      <PageHero>
        <PageHeroContent>
          <PageTitle>
            <span>Heartbeat</span> Journey
          </PageTitle>
          <PageSubtitle>
            Spreading Hope, One Step at a Time - A 7-year mission to transform lives across Morocco
          </PageSubtitle>
          <HeroMeta>
            <MetaItem>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              12 Regions of Morocco
            </MetaItem>
            <MetaItem>
              <FontAwesomeIcon icon={faCalendarAlt} />
              2024 - 2031
            </MetaItem>
            <MetaItem>
              <FontAwesomeIcon icon={faSchool} />
              24 Schools
            </MetaItem>
          </HeroMeta>
        </PageHeroContent>
      </PageHero>
      <MainContent>
        <ProjectOverview>
          <OverviewTitle>
            A Journey of <span>Big Hearts</span>
          </OverviewTitle>
          <OverviewText>
            From 2024 to 2031, across the 12 regions of Morocco, Heartbeat Journey is set on a mission to transform lives.
            With a goal of renovating 24 schools, this project brings hope, care, and change to the most remote villages.
            It is not just about offering aid, it is about building a brighter, more dignified future for those who need it most.
          </OverviewText>

          <ProjectStats>
            <StatCard>
              <StatIcon>
                <FontAwesomeIcon icon={faSchool} />
              </StatIcon>
              <StatNumber>24</StatNumber>
              <StatLabel>Schools to Renovate</StatLabel>
            </StatCard>
            <StatCard>
              <StatIcon>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </StatIcon>
              <StatNumber>12</StatNumber>
              <StatLabel>Regions to Cover</StatLabel>
            </StatCard>
            <StatCard>
              <StatIcon>
                <FontAwesomeIcon icon={faUsers} />
              </StatIcon>
              <StatNumber>2000+</StatNumber>
              <StatLabel>Children to Be Impacted</StatLabel>
            </StatCard>
            <StatCard>
              <StatIcon>
                <FontAwesomeIcon icon={faHeart} />
              </StatIcon>
              <StatNumber>7</StatNumber>
              <StatLabel>Years of Impact to Come</StatLabel>
            </StatCard>
          </ProjectStats>

          {/* <PartnersSection>
            <PartnersTitle>
              Partners and sponsors who placed their <span>trust in us</span>
            </PartnersTitle>
            <PartnersSubtitle>
              Organizations that believe in our mission and support our journey to transform lives across Morocco
            </PartnersSubtitle>
            <PartnersGrid>
              <PartnerLogoContainer
                href="https://www.intelcia.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <PartnerLogo
                  src={process.env.PUBLIC_URL + "/images/logo-sponsor-collab/intelcia-logo.png"}
                  alt="Intelcia - Strategic Partner"
                />
              </PartnerLogoContainer>
              Add more partner logos here as they join
            </PartnersGrid>
          </PartnersSection> */}

          <SponsorSection>
            <SponsorTitle>Project Sponsor</SponsorTitle>
            <a 
              href="https://www.intelcia.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'inline-block' }}
            >
              <SponsorLogo 
                src={process.env.PUBLIC_URL + "/images/logo-sponsor-collab/intelcia-logo.png"} 
                alt="Intelcia - Project Sponsor"
              />
            </a>
          </SponsorSection>
        </ProjectOverview>
        <FiltersSection>
          <SearchBar>
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
        </FiltersSection>
        <CampaignsGrid>
          {filteredProjects.map((project, index) => (
            <CampaignCard key={project.id} index={index}>
              <CampaignImage image={project.image} />
              <CampaignContent>
                <CampaignTitle>{project.title}</CampaignTitle>
                <CampaignMeta>
                  <div>
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {project.location}
                  </div>
                  <div>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {project.date}
                  </div>
                </CampaignMeta>

                {/* Stats section (conditionally displayed) */}
                {project.showStats ? (
                  <>
                    <ProgressBar>
                      <ProgressFill $progress={calculateProgress(project.raised, project.goal)} />
                    </ProgressBar>
                    <CampaignStats>
                      <div>
                        <h4>MAD{project.raised.toLocaleString()}</h4>
                        <p>Raised</p>
                      </div>
                      <div>
                        <h4>{project.donors}</h4>
                        <p>Donors</p>
                      </div>
                      <div>
                        <h4>{calculateDaysLeft(project.eventDate)}</h4>
                        <p>Days Left</p>
                      </div>
                    </CampaignStats>
                  </>
                ) : (
                  // Meaningful placeholder for campaigns without stats
                  <div style={{ margin: "1.5rem 0", color: "#666", fontSize: "0.9rem" }}>
                    A long-term initiative to bring positive change across Morocco.
                  </div>
                )}

                {/* Spacer pushes button to bottom */}
                <Spacer />

                <DonateButton to={`/heartbeat-journey/project/${project.id}`}>
                  Learn More
                </DonateButton>
              </CampaignContent>
            </CampaignCard>
          ))}
        </CampaignsGrid>
      </MainContent>
    </PageWrapper>
  );
};

export default HbeatJourneyPage;
